import PopUpNotification from '@advanza/interfaces/PopUpNotification'
import { formatSendAtForCountry, timeZoneLabelForCountry } from 'misc/notificationSendAt'

interface Props {
    notification: PopUpNotification
    imageUrl?: string | null
}

/**
 * Static mock of the lock-screen banner a provider sees. Purely presentational — nothing is sent
 * and no test account is involved.
 */
const PushNotificationPreview = ({ notification, imageUrl }: Props) => {
    const appName = notification.country_code === 'NL' ? 'Trustoo' : 'Trustlocal'

    return (
        <div className="grid gap-4">
            <div className="rounded-2xl bg-gradient-to-br from-slate-700 to-slate-900 p-5">
                <div className="rounded-2xl bg-white/95 p-3 shadow-lg backdrop-blur">
                    <div className="flex items-start gap-3">
                        <div className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-blue-600 text-sm font-bold text-white">
                            {appName.slice(0, 1)}
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="flex items-baseline justify-between gap-2">
                                <span className="truncate text-sm font-semibold uppercase tracking-wide text-neutral-500">
                                    {appName}
                                </span>
                                <span className="flex-none text-sm text-neutral-400">now</span>
                            </div>
                            <div className="mt-0.5 break-words text-md font-bold text-neutral-900">
                                {notification.push_title || 'Push title'}
                            </div>
                            <div className="mt-0.5 break-words text-md text-neutral-700">
                                {notification.push_body || 'Push message'}
                            </div>
                        </div>
                        {imageUrl && (
                            <img
                                src={imageUrl}
                                alt=""
                                className="h-10 w-10 flex-none rounded-md object-cover"
                            />
                        )}
                    </div>
                </div>
            </div>
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                <p className="m-0 font-semibold">iOS recipients see no image</p>
                <p className="m-0 mt-1">
                    Android renders the header image as shown above. On iOS the notification arrives
                    with the title and message only, so the copy has to work without the image.
                </p>
            </div>
            {notification.push_send_at && (
                <p className="m-0 text-sm text-neutral-600">
                    Scheduled for{' '}
                    {formatSendAtForCountry(notification.push_send_at, notification.country_code)} (
                    {timeZoneLabelForCountry(notification.country_code)}).
                </p>
            )}
        </div>
    )
}

export default PushNotificationPreview
